/*
This XML-RPC client is created based on davexmlrpc Node.js server-client version
NPM: https://www.npmjs.com/package/davexmlrpc
GitHub: https://github.com/scripting/xml-rpc

Due to a lack of async functions client code is extracted, converted to valid TS code
and changed so http requests can work using AXIOS

Functions like: getXmlValue, xmlRpcBuildCall, xmlRpcGetValue, parseReturnedXml are third party functions

Client is using also third party utils helpers extracted from original repo.
*/

const xml2js = require('xml2js');

import axios from 'axios';
import utils from './utils';

function encode(s: any) {
  return utils.encodeXml(s);
}

function getXmlValue(theValue: any) {
  // xmlize a JavaScript value
  let xmltext = '';

  function add(s: any) {
    xmltext += s;
  }

  switch (typeof theValue) {
    case 'string':
      return '<string>' + encode(theValue) + '</string>';
    case 'boolean':
      return '<boolean>' + utils.getBoolean(theValue) + '</boolean>';
    case 'number':
      if (Number.isInteger(theValue)) {
        return '<i4>' + theValue + '</i4>';
      } else {
        return '<double>' + theValue + '</double>';
      }
    case 'object':
      if (Array.isArray(theValue)) {
        add('<array>');
        add('<data>');
        theValue.forEach((element, index) => {
            add('<value>' + getXmlValue(theValue[index]) + '</value>');
        });
        add('</data>');
        add('</array>');
        return xmltext;
      } else {
        if (Buffer.isBuffer(theValue)) {
          return '<base64>' + theValue.toString('base64') + '</base64>';
        } else {
          if (theValue instanceof Date) {
            return (
              '<dateTime.iso8601>' +
              theValue.toISOString() +
              '</dateTime.iso8601>'
            );
          } else {
            add('<struct>');
            for (const x in theValue) {
                if (theValue.hasOwnProperty(x)) {
              add('<member>');
              add('<name>' + encode(x) + '</name>');
              add('<value>' + getXmlValue(theValue[x]) + '</value>');
              add('</member>');
            }
            }
            add('</struct>');
            return xmltext;
          }
        }
      }
    default:
      return '<base64>' + btoa(theValue) + '</base64>';
  }
}

function xmlRpcBuildCall(verb: any, params: any) {
    let xmltext = '';

    function add(s: any) {
        xmltext += s;
    }
    if (params === undefined) {
        params = new Object();
    }
    add('<?xml version="1.0"?>');
    add('<methodCall>');
    add('<methodName>' + encode(verb) + '</methodName>');
    add('<params>');
    for (const x in params) {
        if (params.hasOwnProperty(x)) {
            add('<param>');
            add('<value>' + getXmlValue(params[x]) + '</value>');
            add('</param>');
        }
    }
    add('</params>');
    add('</methodCall>');

    return xmltext;
}

function xmlRpcGetValue(value: any) {
  // get a JavaScript value from an XML-specified value
  // Changes
  // 6/9/18; 1:53:14 PM by DW
  // The XML-RPC validator found a problem -- a <struct> can be empty, i.e. has no members. It's legal.
  let returnedValue;
  function parseDate(theString: any) {
    let theDate = Date.parse(theString);
    if (isNaN(theDate)) {
      const datePart = utils.stringNthField(theString, 'T', 1);
      const timePart = utils.stringNthField(theString, 'T', 2);
      const year = utils.stringMid(datePart, 1, 4);
      const month = utils.stringMid(datePart, 5, 2);
      const day = utils.stringMid(datePart, 7, 2);
      theString = year + '-' + month + '-' + day + 'T' + timePart;
      theDate = Date.parse(theString);
    }
    return new Date(theDate);
  }
  function badResponse(whereBad: any) {
    return 'xmlRpcGetValue:badResponse: whereBad == ' + whereBad;
  }
  for (const x in value) {
    if (value.hasOwnProperty(x)) {
    switch (x) {
      case 'i4':
      case 'int':
      case 'double': // all numbers in JavaScript are floating point
        returnedValue = Number(value[x]);
        break;
      case 'string':
        returnedValue = value[x];
        break;
      case 'boolean':
        returnedValue = utils.getBoolean(value[x]);
        break;
      case 'dateTime.iso8601':
        returnedValue = parseDate(value[x]);
        break;
      case 'base64':
        const buf = new Buffer(value[x], 'base64');
        returnedValue = buf.toString();
        break;
      case 'struct':
        const myObj: {[index: string]: any} = {};
        // returnedValue = new Object();
        let member = value[x].member;
        if (member !== undefined) {
          // 6/9/18 by DW
          if (!Array.isArray(member)) {
            // 5/24/18 by DW
            member = [member];
          }
          for (const i in member) {
            if (member.hasOwnProperty(i)) {
            const item = member[i];
            myObj[item.name] = xmlRpcGetValue(item.value);
            returnedValue = myObj;
            }
          }
        }
        returnedValue = myObj;
        break;
      case 'array':
        returnedValue = new Array();
        const data = value[x].data;
        if (data !== undefined) {
          let newValue = data.value;
          if (newValue !== undefined) {
            if (!Array.isArray(newValue)) {
              newValue = [newValue];
            }
            for (const i in newValue) {
                if (newValue.hasOwnProperty(i)) {
                    returnedValue.push(xmlRpcGetValue(newValue[i]));
                }
              }
          }
        } else {
          badResponse('value');
        }
        }
    }
  }
  if (returnedValue === undefined) {
    // the default type is string
    returnedValue = value;
  }
  return returnedValue;
}

function parseReturnedXml(xmltext: any) {
  const options = {
    explicitArray: false,
  };
  let res;

  xml2js.parseString(xmltext, options,  (err: any, jstruct: any) => {

    function badResponse(whereBad: any) {
      const responseErr = {
        message:
          'Bad response to XML-RPC call, missing ' + whereBad + ' element.',
      };
      res = responseErr;
      return responseErr;
    }

    let returnedValue;
    const methodResponse = jstruct.methodResponse;

    if (methodResponse !== undefined) {
      const params = methodResponse.params;
      if (params !== undefined) {
        const param = params.param;
        if (param !== undefined) {
          const value = param.value;
          if (value !== undefined) {
            returnedValue = xmlRpcGetValue(value);
            res = returnedValue;
            return returnedValue;
          } else {
            badResponse('value');
          }
        } else {
          badResponse('param');
        }
      } else {
        const fault = methodResponse.fault;
        if (fault !== undefined) {
          const value = fault.value;
          if (value !== undefined) {
            returnedValue = xmlRpcGetValue(value);
            if (returnedValue.faultString !== undefined) {
              returnedValue.message = returnedValue.faultString;
            }
            res = returnedValue;
            return returnedValue;
          } else {
            badResponse('value');
          }
        } else {
          badResponse('params or fault');
        }
      }
    } else {
      badResponse('methodResponse');
    }
  });
  return res;
}

function xmlRpcClient(urlEndpoint: any, verb: any, params: any) {
  const rpctext = xmlRpcBuildCall(verb, params);

  const axiosInstance = axios.request({
    url: urlEndpoint,
    method: 'post',
    data: rpctext,
    transformResponse: [
      (data) => {
        // transform the response
        const a = parseReturnedXml(data);
        return a;
      },
    ],
  });

  return axiosInstance;
}

export { xmlRpcClient };
